import { TextField } from '@mui/material'
import { styled } from '../../utils/styled'
import { type CSSObject } from '@mui/material/styles'
import { type Theme } from '../theme'
import { type color } from './types'
import { type Variant } from '../../components/Typography/types'

const defaultFieldsetBorder = (theme: Theme): string => `1px solid ${theme.tokens.border.main.tonal}`

const focusFieldset = (theme: Theme): CSSObject => ({
  border: `1px solid ${theme.tokens.border.focus.brand.primary}`,
  boxShadow: `0 0 0 3px ${theme.tokens.border.focus.brand.halo}`
})

/** Empty placeholder surface — subdued grey (`stone100` → `background.tonal.soft.default`). */
const getInputEmptyBackground = (theme: Theme): string | undefined => theme.tokens.background.tonal.soft.default

/** Filled input surface — main page/surface colour (`white0` → `background.main.default` in both modes). */
const getInputFilledBackground = (theme: Theme): string | undefined => theme.tokens.background.main.default

const getInputBackground = (hasValue: boolean, theme: Theme): string | undefined =>
  hasValue ? getInputFilledBackground(theme) : getInputEmptyBackground(theme)

export interface StyledInputStyleProps {
  value?: string | number
  color: color
  $assistive: boolean
  $fontSizeVariant?: Variant
  $backgroundColor?: string
  $isRightIconClickable: boolean
  $hasLeftIcon: boolean
  $hasRightIcon: boolean
}

export const StyledInput = styled(TextField)<StyledInputStyleProps>(
  ({
    theme,
    value,
    color,
    $assistive: assistive,
    $fontSizeVariant: fontSizeVariant,
    $backgroundColor: backgroundColor,
    $isRightIconClickable: isRightIconClickable,
    $hasLeftIcon: hasLeftIcon,
    $hasRightIcon: hasRightIcon
  }): CSSObject => {
    const stringValue = value?.toString() ?? ''
    const rightIconPadding = hasRightIcon ? '14px' : undefined
    const leftIconPadding = hasLeftIcon ? '8px' : undefined
    const isValidationColor = color === 'success' || color === 'error'

    return {
      '.MuiOutlinedInput-root': {
        borderRadius: assistive ? theme.borderRadius.xLarge : theme.borderRadius.medium,
        padding: 0,
        backgroundColor: backgroundColor ?? getInputBackground(stringValue.length > 0, theme),
        ...(assistive && {
          height: '56px'
        }),
        paddingRight: isRightIconClickable ? '6px' : rightIconPadding,
        paddingLeft: assistive ? '16px' : leftIconPadding,
        height: assistive && '56px',
        input: {
          ...InputOrTextAreaStyles(stringValue, assistive, hasLeftIcon, theme, fontSizeVariant)
        },
        textarea: {
          ...InputOrTextAreaStyles(stringValue, assistive, hasLeftIcon, theme, fontSizeVariant)
        },
        fieldset: {
          border: defaultFieldsetBorder(theme),
          lineHeight: assistive ? theme.typography.h4.lineHeight : theme.typography.body2.lineHeight
        },
        '&:hover': {
          backgroundColor: backgroundColor ?? theme.tokens.background.main.pressed,
          border: 'none',
          ...(!isValidationColor && {
            fieldset: {
              border: defaultFieldsetBorder(theme)
            }
          }),
          ...(color === 'success' && {
            fieldset: {
              border: `1px solid ${theme.tokens.border.success.primary}`,
              boxShadow: `0 0 0 3px ${theme.tokens.border.success.halo}`
            }
          }),
          ...(color === 'error' && {
            fieldset: {
              border: `1px solid ${theme.tokens.border.error.primary}`,
              boxShadow: `0 0 0 3px ${theme.tokens.border.error.halo}`
            }
          })
        },
        '&:focus': {
          backgroundColor: backgroundColor ?? getInputFilledBackground(theme),
          ...(!isValidationColor && {
            fieldset: focusFieldset(theme)
          })
        },
        ...(color === 'success' && {
          ...StyledSuccessInput(theme)
        }),
        ...(color === 'error' && {
          ...StyledErrorInput(theme)
        }),
        ...(backgroundColor === 'transparent' && {
          ...StyledTransparentBackgroundInput()
        })
      },
      '.MuiOutlinedInput-root.Mui-focused': {
        backgroundColor: getInputFilledBackground(theme),
        ...(!isValidationColor && {
          fieldset: focusFieldset(theme)
        }),
        ...(color === 'success' && {
          ...SuccessInputFocus(theme)
        }),
        ...(color === 'error' && {
          ...ErrorInputFocus(theme)
        }),
        ...(backgroundColor === 'transparent' && {
          ...StyledTransparentBackgroundFocus()
        })
      },
      '.MuiOutlinedInput-root.Mui-disabled': {
        backgroundColor: theme.tokens.background.main.pressed,
        color: theme.tokens.foreground.main.disabled,
        fieldset: {
          border: 'none'
        }
      },
      'input:invalid + fieldset': {
        ...ErrorFieldSet(theme)
      },
      'input:invalid:focus + fieldset': {
        ...ErrorFieldSet(theme)
      },
      'input:invalid:hover + fieldset': {
        ...ErrorFieldSet(theme, true)
      }
    }
  }
)

const InputOrTextAreaStyles = (
  value: string,
  assistive: boolean,
  hasLeftIcon: boolean,
  theme: Theme,
  fontSizeVariant?: Variant
): CSSObject => ({
  fontFamily: assistive ? theme.typography.h4.fontFamily : theme.typography.body2.fontFamily,
  fontSize: assistive ? theme.typography.h4.fontSize : theme.typography.body2.fontSize,
  height: assistive ? theme.typography.h4.lineHeight : theme.typography.body2.lineHeight,
  ...(fontSizeVariant != null && {
    fontFamily: theme.typography[fontSizeVariant].fontFamily,
    fontSize: theme.typography[fontSizeVariant].fontSize,
    lineHeight: theme.typography[fontSizeVariant].lineHeight,
    height: theme.typography[fontSizeVariant].lineHeight,
    letterSpacing: theme.typography[fontSizeVariant].letterSpacing
  }),
  padding: '8px 16px',
  paddingLeft: assistive ? '16px' : hasLeftIcon ? '4px' : undefined,
  color: theme.tokens.foreground.main.primary,
  '&::placeholder': {
    color: value.length > 0 ? theme.tokens.foreground.main.primary : theme.tokens.foreground.main.secondary
  }
})

const StyledTransparentBackgroundInput = (): CSSObject => ({
  paddingRight: 0,
  '> input, textarea': {
    border: 'none',
    padding: 0
  },
  fieldset: {
    border: 'none',
    boxShadow: 'none'
  }
})

const StyledTransparentBackgroundFocus = (): CSSObject => ({
  fieldset: {
    border: 'none',
    boxShadow: 'none'
  }
})

const StyledSuccessInput = (theme: Theme): CSSObject => ({
  backgroundColor: getInputFilledBackground(theme),
  fieldset: {
    border: `1px solid ${theme.tokens.border.success.primary}`
  },
  '&:hover': {
    fieldset: {
      border: `1px solid ${theme.tokens.border.success.primary}`,
      boxShadow: `0 0 0 3px ${theme.tokens.border.success.halo}`
    }
  }
})

const SuccessInputFocus = (theme: Theme): CSSObject => ({
  fieldset: {
    border: `1px solid ${theme.tokens.border.success.primary}`,
    boxShadow: `0 0 0 3px ${theme.tokens.border.success.halo}`
  }
})

const ErrorFieldSet = (theme: Theme, isHover = false): CSSObject => ({
  border: `1px solid ${theme.tokens.border.error.primary}`,
  ...(isHover && { boxShadow: `0 0 0 3px ${theme.tokens.border.error.halo}` })
})

const StyledErrorInput = (theme: Theme): CSSObject => ({
  backgroundColor: getInputFilledBackground(theme),
  fieldset: {
    ...ErrorFieldSet(theme)
  },
  '&:hover': {
    fieldset: {
      ...ErrorFieldSet(theme, true)
    }
  }
})

const ErrorInputFocus = (theme: Theme): CSSObject => ({
  fieldset: {
    border: `1px solid ${theme.tokens.border.error.primary}`,
    boxShadow: `0 0 0 3px ${theme.tokens.border.error.halo}`
  }
})

export const StyledLabel = styled('div')(
  ({ theme }): CSSObject => ({
    color: theme.tokens.foreground.main.primary,
    fontFamily: theme.typography.h5.fontFamily,
    fontSize: theme.typography.h5.fontSize,
    marginBottom: '8px'
  })
)

export const StyledMessage = styled('div')(
  ({ theme }): CSSObject => ({
    color: theme.tokens.foreground.main.primary,
    display: 'flex',
    alignItems: 'center',
    fontFamily: theme.typography.body3.fontFamily,
    fontSize: theme.typography.body3.fontSize,
    marginTop: '8px'
  })
)

export const StyledClearButton = styled('button')(
  ({ theme }): CSSObject => ({
    cursor: 'pointer',
    display: 'flex',
    border: 'none',
    padding: '0',
    backgroundColor: 'transparent',
    color: theme.tokens.foreground.main.primary,
    '&:disabled': {
      cursor: 'default',
      color: theme.tokens.foreground.main.disabled
    }
  })
)
